import { useEffect, useState } from 'react'
import { useFormikContext } from 'formik'
import {
  Alert,
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
} from '@mui/material'
import { Id } from 'types/Id'
import { Close, Download, Warehouse } from '@mui/icons-material'
import opportunitiesResource from '@chunker/chunker-request/dist/opportunitiesResource'
import { PPSServer } from 'lib/pps'
import { OpportunitySolution as CSOpportunitySolution } from '@chunker/chunker-request/dist/types/OpportunitySolution'
import FormHeaderDivider from 'components/FormHeaderDivider'
import FormHeaderStep from 'components/FormHeaderStep'
import FormikTextFieldEditable from 'components/FormikTextFieldEditable'
import { DeepPartial } from 'types/DeepPartial'
import { Opportunity } from '@chunker/chunker-request'
import useNotify from 'hooks/useNotify'
import HeaderSmall from 'components/HeaderSmall'
import { CampaignConfig, CampaignDefaults, PPSOpportunitySolution } from 'lib/pps/types'
import OutlinedContainer from 'components/OutlinedContainer'
import FormAccordionOption from 'components/FormAccordionOption'
import CampaignDashboard from 'components/CampaignDashboard'
import OpportunityBackNextButtons from '../OpportunityActionsButtons'
import WarehouseCustomerLocationsTable from './WarehouseCustomerLocationsTable'
import WarehouseChunkerMarketPlacesTable from './WarehouseChunkerMarketPlacesTable'
import { ReinitializeOpportunity } from '../types'
import DownloadSolutionContactedCSVButton from './DownloadSolutionContactedCSVButton'

function WarehouseSolutions({
  onClose,
  reinitializeOpportunity,
}: {
  onClose: () => void
  reinitializeOpportunity: ReinitializeOpportunity
}) {
  const { notifyError, notifySuccess } = useNotify()
  const { values } = useFormikContext<DeepPartial<Opportunity>>()
  const [loadingPPS, setLoadingPPS] = useState(true)
  const [loadingCS, setLoadingCS] = useState(true)
  const [customerLocations, setCustomerLocations] = useState<
    CSOpportunitySolution['customerLocations']
  >([])

  const [chunkerMarketPlaces, setChunkerMarketPlaces] = useState<
    CSOpportunitySolution['chunkerMarketPlaces']
  >([])
  const [ppsOpportunitySolution, setPpsOpportunitySolution] =
    useState<PPSOpportunitySolution | null>(null)
  const [campaignDefaults, setCampaignDefaults] = useState<CampaignDefaults | undefined>()
  const [opportunitySolutionFetched, setOpportunitySolutionFetched] = useState(false)
  const [warehouseLocationsFetched, setWarehouseLocationsFetched] = useState(false)
  const [errorLoadingOpportunitySolution, setErrorLoadingOpportunitySolution] = useState(false)
  const opportunityId = values.id
  const { warehouseLocation } = values
  const solutionCompleted = ppsOpportunitySolution && ppsOpportunitySolution.status === 'COMPLETE'
  const isFirstRun = !ppsOpportunitySolution

  const handleSubmitCampaign = async (config: CampaignConfig) => {
    if (!opportunityId) {
      notifyError('Opportunity ID is missing')
      return
    }

    try {
      const data = await PPSServer.submitOpportunitySolution(opportunityId, config)
      setPpsOpportunitySolution(data)
      notifySuccess?.('Campaign submitted successfully')
    } catch (err) {
      console.error(err)
      notifyError('An error occurred while submitting the outreach campaign.')
    }
  }

  const handleRerun = async (config: CampaignConfig) => {
    const solutionId = ppsOpportunitySolution?._id
    if (!solutionId) {
      notifyError('Opportunity solution ID is missing')
      return
    }

    try {
      const data = await PPSServer.rerunOpportunitySolution(solutionId, config)
      setPpsOpportunitySolution(data)
      notifySuccess?.('Re-run campaign initiated successfully')
    } catch (err) {
      console.error(err)
      notifyError('An error occurred while triggering the re-run campaign.')
    }
  }

  const handleRefresh = async () => {
    if (!opportunityId) return

    setLoadingPPS(true)
    setErrorLoadingOpportunitySolution(false)

    try {
      const data = await PPSServer.getOpportunitiesSolutionById(opportunityId)
      if (Object.keys(data).length) {
        setPpsOpportunitySolution(data)
      }
    } catch (err) {
      console.error(err)
      setErrorLoadingOpportunitySolution(true)
      notifyError('An error occurred while refreshing the campaign data.')
    } finally {
      setLoadingPPS(false)
    }
  }

  // Fetch campaign defaults on mount
  useEffect(() => {
    PPSServer.getOpportunitySolutionDefaults()
      .then(setCampaignDefaults)
      .catch((err) => {
        console.error('Failed to fetch campaign defaults:', err)
      })
  }, [])

  useEffect(() => {
    if (!opportunityId) return
    if (!warehouseLocation) {
      notifyError(
        'This form currently supports warehouse opportunities only. Please provide a location for the warehouse location the customer is asking for.',
      )
      return
    }

    if (!opportunitySolutionFetched) {
      setLoadingPPS(true)

      PPSServer.getOpportunitiesSolutionById(opportunityId)
        .then((data) => {
          if (Object.keys(data).length) setPpsOpportunitySolution(data)
        })
        .catch((err) => {
          console.error(err)
          setErrorLoadingOpportunitySolution(true)
          notifyError(
            'An error occurred while getting the opportunity solution. If this problem persists, please contact support.',
          )
        })
        .finally(() => {
          setLoadingPPS(false)
          setOpportunitySolutionFetched(true)
        })
    }

    if (!warehouseLocationsFetched) {
      setLoadingCS(true)

      opportunitiesResource.requests
        .getOpportunitiesByIdSolution(opportunityId)
        .then(({ data }) => {
          setCustomerLocations(data.customerLocations)
          setChunkerMarketPlaces(data.chunkerMarketPlaces)
        })
        .catch((err) => {
          console.error(err)
          notifyError('An error occurred while searching for a warehouse solution.')
        })
        .finally(() => {
          setLoadingCS(false)
          setWarehouseLocationsFetched(true)
        })
    }
  }, [
    notifyError,
    opportunityId,
    opportunitySolutionFetched,
    warehouseLocation,
    warehouseLocationsFetched,
  ])

  return (
    <>
      <DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (t) => t.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
        <FormHeaderStep
          step="3"
          title="Warehouse Solutions"
          description="Optimized warehouse solutions aligned with location requirements"
          Icon={Warehouse}
        />
        <FormHeaderDivider>Automated Submission (Outreach Campaign)</FormHeaderDivider>
        <HeaderSmall>
          Identifies industrial building locations that meet opportunity criteria, including those
          in the Chunker Marketplace and the broader Chunker ecosystem.
        </HeaderSmall>
        <Box sx={{ display: 'flex' }}>
          <DialogContent sx={{ px: 1 }}>
            {loadingPPS && <LinearProgress sx={{ mb: 2 }} />}
            {!loadingPPS && errorLoadingOpportunitySolution && (
              <Alert severity="error" sx={{ mb: 2 }}>
                An error occurred while loading the campaign. Please try again.
              </Alert>
            )}
            {!loadingPPS && !errorLoadingOpportunitySolution && (
              <>
                {/* Campaign Dashboard - handles both first run and re-run */}
                <CampaignDashboard
                  opportunitySolution={ppsOpportunitySolution}
                  onSubmit={handleSubmitCampaign}
                  onRerun={handleRerun}
                  onRefresh={() => {
                    handleRefresh().catch(console.error)
                  }}
                  isFirstRun={isFirstRun}
                  loading={loadingPPS}
                  defaults={campaignDefaults}
                />

                {/* Download CSV - only show when campaign exists and is complete */}
                {solutionCompleted && (
                  <OutlinedContainer sx={{ mt: 3 }}>
                    <HeaderSmall>Download Campaign Results</HeaderSmall>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DownloadSolutionContactedCSVButton
                        opportunityId={opportunityId as Id}
                        opportunityName={values.name || ''}
                        disabled={!solutionCompleted}
                        variant="outlined"
                        size="small"
                        startIcon={<Download />}
                      >
                        Download Contacted CSV
                      </DownloadSolutionContactedCSVButton>
                    </Box>
                  </OutlinedContainer>
                )}
              </>
            )}
          </DialogContent>
        </Box>
        <FormHeaderDivider>Manual Submission (Generate CSV)</FormHeaderDivider>
        <FormAccordionOption title="Manual">
          <HeaderSmall>
            Locations within the Chunker system—use this step only for opportunities without a
            submitted outreach campaign.
          </HeaderSmall>
          <FormikTextFieldEditable id="name" name="name" label="Opportunity Name" required />
          <DialogContent>
            {loadingCS ? <LinearProgress /> : null}
            {!loadingCS && customerLocations?.length === 0 && chunkerMarketPlaces?.length === 0 && (
              <Alert severity="warning" variant="filled" sx={{ mb: 4 }}>
                No warehouse solutions found. Please add a preferred WAREHOUSE location on the
                opportunity form to search for warehouse solutions.
              </Alert>
            )}
            {chunkerMarketPlaces && chunkerMarketPlaces?.length > 0 && (
              <>
                <FormHeaderDivider sx={{ my: 3 }}>
                  Preferred Locations - Chunker Marketplace
                </FormHeaderDivider>
                {loadingCS ? (
                  <LinearProgress />
                ) : (
                  <WarehouseChunkerMarketPlacesTable locations={chunkerMarketPlaces} />
                )}
              </>
            )}
            {customerLocations && customerLocations.length > 0 && (
              <>
                <FormHeaderDivider sx={{ my: 3 }}>
                  Other Locations - HubSpot customers
                </FormHeaderDivider>
                {loadingCS ? (
                  <LinearProgress />
                ) : (
                  <WarehouseCustomerLocationsTable locations={customerLocations} />
                )}
              </>
            )}
          </DialogContent>
        </FormAccordionOption>
      </DialogTitle>
      <DialogActions>
        <OpportunityBackNextButtons
          action="back"
          reinitializeOpportunity={reinitializeOpportunity}
        />
      </DialogActions>
    </>
  )
}

export default WarehouseSolutions
